import { Link } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import { all_routes } from "../../router/all_routes";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin, { DateClickArg } from "@fullcalendar/interaction";
import useApi from "../../../core/customhook/useApi";
import dayjs from "dayjs";
import { Table, Modal } from "antd";
import { TimePicker } from 'antd';
import Swal from "sweetalert2";


export function decimalHoursToDayjs(decimalHours: number): dayjs.Dayjs {
    const hours = Math.floor(decimalHours);
    let minutes = Math.round((decimalHours - hours) * 60);

    // normalize edge case (e.g. 10.999 → 11:00)
    let h = hours;
    if (minutes === 60) {
        h += 1;
        minutes = 0;
    }

    return dayjs().hour(h).minute(minutes).second(0);
}

function hhmmToDecimalHours(time: string): number {
    const [hoursStr, minutesStr] = time.split(':');
    const hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10);

    const decimal = hours + minutes / 60;

    return Math.floor(decimal * 100) / 100;
}

function decimalHoursToHHMM(decimalHours: number): string {
    const hours = Math.floor(decimalHours);

    const minutes = Math.round((decimalHours - hours) * 60);

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

const TASK_COLORS = [
    "#28a745",
    "#007bff",
    "#fd7e14",
    "#6f42c1",
    "#20c997",
    "#dc3545",
    "#17a2b8",
];

const TimeSheetsLog = () => {
    const [selectedTask, setSelectedTask] = useState<any>(null);
    const [calendarEvents, setCalendarEvents] = useState<any[]>([]);
    const [selectedDate, setSelectedDate] = useState<string>("");
    const [projectData, setProjectData] = useState<any>([])
    const [selectedProject, setSelectedProject] = useState<any>(null);
    const [task, setTask] = useState<any>([])
    const [hoveredEvent, setHoveredEvent] = useState<any>(null);
    const [hours, setHours] = useState<number | any>();
    const [description, setDescription] = useState("");
    const [attachment, setAttachment] = useState<File[]>([]);

    const [count, setCount] = useState(1)

    const {
        data: attendanceData,
        loading: attendanceLoading,
        error: attendanceErrro,
        callApi: fetchAttendanceData
    } = useApi("/api/timelog-dayvalidate", {
        method: "POST",
        autoCall: false,
    });

    const { data: addTimeLogResponse, callApi: AddTimeLog } = useApi("/api/add-timelog", {
        method: "POST",
        autoCall: false,
    });

    const { data: timeLogData, } = useApi("/api/fetch-timelog", {
        method: "POST",
        autoCall: true,
        dependencies: [count]
    })

    // console.log("Time-Log :", timeLogData)

    const calendarRef = useRef<any>(null)

    const ATTENDANCE_TYPE_LIMITS: Record<string, { min: number; max: number }> = {
        P: { min: 9, max: 24 },
        SH: { min: 7, max: 9 },
        HP: { min: 4.5, max: 7 },
        A: { min: 0, max: 0 },
        WO: { min: 0, max: 0 },
        H: { min: 0, max: 0 },
    };

    const getDayInfo = (date: string) => {
        const record = attendanceData?.data.find((a: any) => a.attendance_date === date);
        const status = record?.status || 'A';

        const totalLoggedHours = timeLogData?.data
            .filter((log: any) => log.log_date === date)  // only logs for this date
            .reduce((sum: number, log: any) => sum + Number(log.log_hours || 0), 0);


        const totalHours = Number(record?.self_regn_hours ?? record?.hours_machine ?? 0);


        const limits = ATTENDANCE_TYPE_LIMITS[status] || { min: 0, max: 24 };
        const maxHours = limits.max;

        // const remainingHours = Math.max(maxHours - totalLoggedHours, 0);
        const canFillLogs = !['WO', 'H', 'A', 'EL', 'RH', 'SL', 'ML', 'LWP', 'BL', 'WL'].includes(status) && totalHours < maxHours;

        return {
            date,
            attendance: status,
            totalHours,
            maxHours,
            totalLoggedHours,
            canFillLogs,
            raw: record
        };
    };

    const getTaskColor = (taskId: number) => {
        return TASK_COLORS[taskId % TASK_COLORS.length];
    };

    const timeLogEvents = useMemo(
        () =>
            (timeLogData?.data || []).map((log: any) => ({
                title: `${log.task_name}`,
                date: log.log_date,
                backgroundColor: getTaskColor(log.task_id),
                extendedProps: {
                    type: "timeLog",
                    id: log.id,
                    project_name: log.project_name,
                    task_id: log.task_id,
                    hour: log.log_hours,
                    notes: log.notes,
                },
            })),
        [timeLogData?.data]
    );

    const submitTimeLog = async () => {
        // Get day info (attendanceRecord based)
        const dayInfo = getDayInfo(selectedDate);

        if (!dayInfo.canFillLogs) {
            Swal.fire({
                icon: "warning",
                title: "Time logging not allowed",
                text: "You cannot add time logs for this day.",
            });
            return;
        }
        const remainingHours = Number(
            (dayInfo.totalHours - dayInfo.totalLoggedHours).toFixed(2)
        );

        console.log("Remaining Hour :", remainingHours)

        if (Number(hours) > remainingHours) {
            Swal.fire({
                icon: "warning",
                title: "Exceeds allowed hours",
                text: `You can log a maximum of ${decimalHoursToHHMM(remainingHours)} hours for this day.`,
            });
            return;
        }

        const formData = new FormData();
        formData.append("project_id", selectedProject.project_id);
        formData.append("task_id", selectedTask.task_id);
        formData.append("log_date", selectedDate);
        formData.append("log_hours", String(hours));
        formData.append("notes", description);

        if (attachment && attachment.length > 0) {
            attachment.forEach((file) => {
                formData.append("files", file);
            });
        }
        // Build payload
        // const formData = {
        //     project_id: selectedProject.project_id,
        //     task_id: selectedTask.task_id,
        //     log_date: selectedDate,
        //     log_hours: String(hours),
        //     notes: description
        // };

        try {
            const response = await AddTimeLog(formData);

            if (response.success) {
                Swal.fire({
                    icon: "success",
                    title: "TimeLog Added",
                    text: response?.message || "Time log added successfully.",
                });

                document.getElementById("openAttendanceModalBtn")?.click();
                setCount(count + 1);

                setHours("");
                setDescription("");
                setAttachment([]);

                const weekStart = dayjs(selectedDate).startOf("week").format("YYYY-MM-DD");
                const weekEnd = dayjs(selectedDate).endOf("week").format("YYYY-MM-DD");
                getAttendanceByWeek(weekStart, weekEnd);

            } else {
                Swal.fire({
                    icon: "warning",
                    title: "Invalid Input",
                    text: response?.message || "Something went wrong.",
                });
            }
            setAttachment([])

        } catch (err) {
            console.error(err);
        }
    };


    const getAttendanceByWeek = (weekStart: string, weekEnd: string) => {
        fetchAttendanceData({
            fromDate: weekStart,
            toDate: weekEnd,
        });
    };

    useEffect(() => {
        if (attendanceData?.success) {
            const attendanceEvents = attendanceData.data.map((record: any) => {
                console.log(record);
                let color =
                    record.status === "P"
                        ? "#28a745" // green
                        : record.status === "A"
                            ? "#dc3545" // red
                            : record.status === "HP"
                                ? "#fd7e14" // orange
                                : record.status === "WO"
                                    ? "#007bff" // blue
                                    : record.status === "SH"
                                        ? "#ffc107" // yellow (for short hour)
                                        : "#6c757d"; // gray fallback

                let inTime = record.in_time_machine;
                let outTime = record.out_time_machine;

                if (
                    record.regularisation_status === "APPROVED" &&
                    record.in_time_machine === "00:00:00" &&
                    record.out_time_machine === "00:00:00"
                ) {
                    inTime = record.self_regn_in_time || "-";
                    outTime = record.self_regn_out_time || "-";
                }

                return {
                    title: record.status,
                    date: record.attendance_date,
                    color: color,
                    extendedProps: {
                        type: "attendance",
                        attendance_log_id: dayjs(record.attendance_log_id).format("DD-MM-YYYY"),
                        status: record.status,
                        in_time: inTime,
                        out_time: outTime,
                    }
                };
            });
            const holidayEvents = attendanceData.holidays?.map((holiday: any) => {
                const isRH = holiday.holiday_category === "RH";

                return {
                    title: holiday.holiday,
                    date: holiday.holiday_date,
                    backgroundColor: "transparent", // optional
                    extendedProps: {
                        type: "attendance",
                        isHoliday: true,
                        category: holiday.holiday_category,
                        textColor: isRH ? "#007bff" : "#dc3545",  // blue or red
                    },
                };
            }) || [];
            const allEvents = [...attendanceEvents, ...holidayEvents];
            setCalendarEvents(allEvents);
        }
    }, [attendanceData])

    const {
        data: projectResponse,
        loading: projectDataLoading,
        error: projectDataError,
    } = useApi("/api/fetch-project-and-task", {
        method: "GET",
        autoCall: true,
    });

    useEffect(() => {
        if (projectResponse?.success) {
            // console.log(projectResponse?.data)
            setProjectData(projectResponse?.data)
            console.log("Task :", projectResponse?.data?.tasks)
            // setTask(projectResponse?.data?.tasks ?? [])

        }
    }, [projectResponse])

    // const {
    //     data: taskData,
    //     loading: taskLoading,
    //     error: taskError,
    // } = useApi("/api/get-all-project-task", {
    //     method: "GET",
    //     autoCall: true,
    //     dependencies: [count]
    // });

    const openModal = (dateStr: string) => {

        if (!selectedProject || !selectedTask) {
            Swal.fire({
                icon: "warning",
                title: "Missing Selection",
                text: "Please select project and task before proceeding.",
            });
            return;
        }

        const response = getDayInfo(dateStr);

        const remainingHours = Number(
            (response.totalHours - response.totalLoggedHours).toFixed(2)
        );

        if (remainingHours < 0.1) {
            Swal.fire({
                icon: "warning",
                title: "Time logging not allowed",
                text: "You cannot add time logs for this day.",
            });
            return;
        }

        if (!response.canFillLogs || remainingHours <= 0) {
            let title = "Time logging not allowed";
            let text = "";

            switch (response.attendance) {
                case 'A':
                    text = "You are marked Absent for this day. Logging hours is not allowed.";
                    break;
                case 'WO':
                    text = "This is a Weekly Off. Time logging is disabled.";
                    break;
                case 'H':
                    text = "This is a Holiday. Time logging is disabled.";
                    break;
                case 'EL':
                    text = "This is an Earned Leave. Time logging is disabled.";
                    break;
                case 'SL':
                    text = "This is a Sick Leave. Time logging is disabled.";
                    break;
                case 'RH':
                    text = "This is a Restricted Holiday. Time logging is disabled.";
                    break;
                case 'ML':
                    text = "This is a Maternity Leave. Time logging is disabled.";
                    break;
                case 'LWP':
                    text = "This is a Leave Without Pay. Time logging is disabled.";
                    break;
                case 'BL':
                    text = "This is a Bereavement Leave. Time logging is disabled.";
                    break;
                case 'WL':
                    text = "This is a Wedding Leave. Time logging is disabled.";
                    break;
                case 'HL':
                    text = "This is a Happiness Leave. Time logging is disabled.";
                    break;
                default:
                    text = "You have already logged the maximum allowed hours for this day.";
            }

            Swal.fire({
                title,
                text,
                icon: "warning",
                confirmButtonText: "OK"
            });
            return;
        }

        // Open modal
        setSelectedDate(dateStr);
        setHours('');
        setDescription('');
        setAttachment([]);

        document.getElementById("openAttendanceModalBtn")?.click();
    };

    // useEffect(() => {
    //     if (!selectedProject) {
    //         setTask([]);
    //         return;
    //     }

    //     const filteredTasks = taskData?.data
    //         .filter((task: any) => task.project_id === selectedProject.project_id)
    //         .map((task: any, index: any) => ({
    //             ...task,
    //             key: task.task_id,
    //             sno: index + 1,
    //         }));

    //     setTask(filteredTasks);
    // }, [selectedProject,]);

    const handleProjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const projectId = Number(e.target.value); // project_id from select

        const project = projectData.find(
            (p: any) => p.project_id === projectId
        );

        if (project) {
            setSelectedProject(project);  // update selected project
            setTask(project.tasks);       // update tasks dropdown

            console.log("Selected Project:", project);
            console.log("Tasks:", project.tasks);
        }
    };

    // const handleProjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    //     const projectId = e.target.value;

    //     const project = projectData.find(
    //         (p: any) => p.project_name === projectId
    //     );

    //     setSelectedProject(project);

    //     console.log("Project :", project)
    //     console.log("Selected Project :", selectedProject)
    // };

    let lastClickTime = 0;

    function decimalHoursToDayjs(decimalHours: number): dayjs.Dayjs {
        const hours = Math.floor(decimalHours);
        let minutes = Math.round((decimalHours - hours) * 60);

        // normalize edge case (e.g. 10.999 → 11:00)
        let h = hours;
        if (minutes === 60) {
            h += 1;
            minutes = 0;
        }

        return dayjs().hour(h).minute(minutes).second(0);
    }

    const dayjsToDecimalHours = (time: any) => {
        if (!time) return null;
        return time.hour() + time.minute() / 60;
    };


    return (
        <>
            <div className="page-wrapper">
                <div className="content">
                    <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
                        <div className="my-auto mb-2">
                            <h2 className="mb-1">Time Logs</h2>
                            <nav>
                                <ol className="breadcrumb mb-0">
                                    <li className="breadcrumb-item">
                                        <Link to={all_routes.adminDashboard}>
                                            <i className="ti ti-smart-home" />
                                        </Link>
                                    </li>
                                    <li className="breadcrumb-item">Time-Sheet</li>
                                    <li className="breadcrumb-item">Time Logs</li>
                                </ol>
                            </nav>
                        </div>
                        <div className="ms-2 head-icons">
                            <CollapseHeader />
                        </div>
                    </div>

                    <div className="card">
                        <div className="card-header  d-flex justify-content-between py-2">
                            <h5>Add Time Log</h5>
                            <Link to="/tasks/tasklogs-details"
                                className="btn btn-primary btn-sm">View Details
                            </Link>
                        </div>

                        <div className="card-body">
                            <div className="d-flex justify-content-start">
                                <select
                                    className="form-select w-auto"
                                    onChange={handleProjectChange}
                                    defaultValue=""
                                >
                                    <option value="" disabled>
                                        Please Select Project
                                    </option>

                                    {projectData.map((value: any) => (
                                        <option
                                            key={value.project_id}
                                            value={value.project_id}
                                        >
                                            {value.project_name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="mt-4">
                                {selectedProject && (
                                    <div className="table-responsive">
                                        <table className="table align-middle">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>S No</th>
                                                    <th>Project Name</th>
                                                    <th>Owner</th>
                                                    <th>Start Date</th>
                                                    <th>End Date</th>
                                                    <th>Attachment</th>
                                                    <th>Priority</th>
                                                    <th>Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>1</td>
                                                    <td>{selectedProject.project_name}</td>
                                                    <td>{selectedProject.owner_name}</td>

                                                    <td>
                                                        {selectedProject.start_date
                                                            ? dayjs(selectedProject.start_date).format("D MMMM YYYY")
                                                            : "N/A"}
                                                    </td>

                                                    <td>
                                                        {selectedProject.end_date
                                                            ? dayjs(selectedProject.end_date).format("D MMMM YYYY")
                                                            : "N/A"}
                                                    </td>

                                                    <td>
                                                        {selectedProject.attachment_file ? (
                                                            <a
                                                                // href={`${apibaseurl}${selectedProject.attachment_file}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-primary text-decoration-underline"
                                                            >
                                                                View
                                                            </a>
                                                        ) : (
                                                            "N/A"
                                                        )}
                                                    </td>

                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <span
                                                                className={`rounded-circle me-2 ${selectedProject.priority === "High"
                                                                    ? "bg-danger"
                                                                    : selectedProject.priority === "Low"
                                                                        ? "bg-success"
                                                                        : "bg-warning"
                                                                    }`}
                                                                style={{ width: 10, height: 10 }}
                                                            />
                                                            <span>{selectedProject.priority}</span>
                                                        </div>
                                                    </td>

                                                    <td>
                                                        <span
                                                            className={`badge ${selectedProject.status === "Active"
                                                                ? "bg-success"
                                                                : "bg-danger"
                                                                }`}
                                                        >
                                                            {selectedProject.status === "Active" ? "Active" : "Inactive"}
                                                        </span>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                            </div>

                            <div className="row mt-4">
                                <div className="col-lg-3">
                                    <div className="card">
                                        <div className="card-header py-2">
                                            <h6 className="mb-0">Tasks</h6>
                                        </div>
                                        <div className="card-body p-2">
                                            <ul className="list-group">
                                                {task.map((task: any) => (
                                                    <li
                                                        key={task?.task_id}
                                                        className="list-group-item d-flex align-items-center"
                                                        style={{ cursor: "pointer" }}
                                                        onClick={() => {
                                                            setSelectedTask(task);
                                                            // console.log("SELECTED :", selectedTask)
                                                        }}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input me-2"
                                                            checked={selectedTask?.task_id === task.task_id}
                                                            readOnly
                                                        />
                                                        {task.task_name}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>

                                    </div>
                                </div>

                                <div className="col-lg-9">
                                    <div className="card">
                                        <div className="card-header d-flex justify-content-between py-2">
                                            <h6 className="mb-0">
                                                {selectedTask
                                                    ? `Time Log: ${selectedTask.task_name}`
                                                    : "Select a task"}
                                            </h6>
                                        </div>

                                        <div className="card-body p-2">

                                            <FullCalendar
                                                ref={calendarRef}
                                                plugins={[dayGridPlugin, interactionPlugin]}
                                                editable={false} // disable drag-and-drop
                                                initialView="dayGridWeek"
                                                headerToolbar={{
                                                    left: 'prev,next today',
                                                    center: 'title',
                                                    right: 'dayGridWeek,dayGridMonth'
                                                }}
                                                height={500}
                                                businessHours={{
                                                    daysOfWeek: [1, 2, 3, 4, 5],
                                                    startTime: "09:00",
                                                    endTime: "17:00",
                                                }}
                                                events={[...timeLogEvents, ...calendarEvents]}

                                                // Make day cells clickable
                                                // dayCellDidMount={(arg) => {
                                                //     arg.el.style.cursor = "pointer";
                                                // }}

                                                // Fetch data when week changes
                                                datesSet={(info) => {
                                                    const weekStart = dayjs(info.start).format("YYYY-MM-DD");
                                                    const weekEnd = dayjs(info.end).subtract(1, "day").format("YYYY-MM-DD");
                                                    getAttendanceByWeek(weekStart, weekEnd);
                                                }}

                                                dayCellContent={({ date }) => {
                                                    const formattedDate = dayjs(date).format("YYYY-MM-DD");

                                                    const dayEvents = calendarEvents.filter(
                                                        (e: any) => e.date === formattedDate
                                                    );

                                                    // console.log('Calendar Event :', calendarEvents)


                                                    // console.log("Day Event :", dayEvents)
                                                    const timeLogEvents = dayEvents.filter(
                                                        (e: any) => e.type === "timeLog"
                                                    );
                                                    // console.log("Day timeLogEvents :", timeLogEvents)

                                                    const attendanceEvent = dayEvents.find(
                                                        (e: any) => e.type !== "timeLog"
                                                    );
                                                    // console.log("Day Event []:", dayEvents);

                                                    // console.log("Day attendanceEvent :", attendanceEvent)

                                                    const statusColors: Record<string, string> = {
                                                        P: "#28a745",
                                                        A: "#dc3545",
                                                        HP: "#fd7e14",
                                                        WO: "#007bff",
                                                        SH: "#ffc107",
                                                    };

                                                    const dotColor = attendanceEvent
                                                        ? attendanceEvent.extendedProps?.isHoliday
                                                            ? attendanceEvent.extendedProps.category === "RH"
                                                                ? "#007bff"
                                                                : "#dc3545"
                                                            : statusColors[attendanceEvent.title] || "#6c757d"
                                                        : null;

                                                    return (
                                                        <div style={{ textAlign: "center" }}>
                                                            {/* Date number */}
                                                            <div>{date.getDate()}</div>

                                                            {/* Attendance / holiday dot */}
                                                            {dotColor && (
                                                                <div style={{ display: "flex", justifyContent: "center", marginTop: 4 }}>
                                                                    <span
                                                                        title={attendanceEvent?.title}
                                                                        style={{
                                                                            width: 10,
                                                                            height: 10,
                                                                            backgroundColor: dotColor,
                                                                            borderRadius: "50%",
                                                                            display: "inline-block",
                                                                        }}
                                                                    />
                                                                </div>
                                                            )}

                                                            {/* Time log wide bars */}
                                                            {timeLogEvents.map((log: any, index: number) => (
                                                                <div
                                                                    key={index}
                                                                    title={log.title}
                                                                    style={{
                                                                        marginTop: 4,
                                                                        height: 6,
                                                                        width: "90%",
                                                                        backgroundColor: log.color || "#ff7a00",
                                                                        borderRadius: 4,
                                                                        marginLeft: "auto",
                                                                        marginRight: "auto",
                                                                    }}
                                                                />
                                                            ))}
                                                        </div>
                                                    );
                                                }}

                                                // Customize event display
                                                eventContent={(eventInfo) => (
                                                    <div
                                                        title={eventInfo.event.title}
                                                        style={{
                                                            color: eventInfo.event.extendedProps.textColor || "white",
                                                            fontWeight: "bold",
                                                            fontSize: 11,
                                                            textAlign: "center",
                                                            userSelect: "none",
                                                            whiteSpace: "nowrap",
                                                            cursor: "pointer"
                                                        }}
                                                    >
                                                        {eventInfo.event.title}
                                                    </div>
                                                )}

                                                // Hover logic
                                                eventMouseEnter={(info) => {
                                                    const e = info.event.extendedProps;
                                                    setHoveredEvent({
                                                        type: e.type,
                                                        date: info.event.startStr,
                                                        status: e.status,
                                                        in_time: e.in_time,
                                                        out_time: e.out_time,
                                                        notes: e.notes,
                                                        hour: decimalHoursToHHMM(e.hour),
                                                        project: e.project_name,
                                                        task_id: e.task_id,
                                                        x: info.jsEvent.pageX,
                                                        y: info.jsEvent.pageY,
                                                    });
                                                }}
                                                eventMouseLeave={() => setHoveredEvent(null)}

                                                // Selectable cells (click / double-click)
                                                // select={(info) => console.log("",info)}
                                                // dateClick={(info) => {
                                                //     if (info.jsEvent.detail === 2) {
                                                //         openModal(dayjs(info.date).format("YYYY-MM-DD"));
                                                //     }
                                                // }}
                                                // Double-click day to open modal
                                                dateClick={(info: DateClickArg) => {
                                                    const now = Date.now();
                                                    if (now - lastClickTime < 300) {
                                                        openModal(dayjs(info.date).format("YYYY-MM-DD"));
                                                    }
                                                    lastClickTime = now;
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {/* footer */}
                <div className="footer d-flex align-items-center justify-content-center   border-top bg-white p-3">
                    <p className="mb-0">© {new Date().getFullYear()} WeConnect. All rights reserved.</p>
                </div>
            </div>


            {hoveredEvent && (
                <div
                    style={{
                        position: "absolute",
                        top: hoveredEvent.y + 10,
                        left: hoveredEvent.x + 10,
                        background: "white",
                        border: "1px solid #ccc",
                        borderRadius: "6px",
                        padding: "8px 12px",
                        boxShadow: "0px 2px 10px rgba(0,0,0,0.1)",
                        zIndex: 9999,
                        fontSize: "13px",
                        minWidth: "200px",
                        // pointerEvents: "none",
                    }}
                >
                    {hoveredEvent.type === "timeLog" ? (
                        <>
                            <strong>Project name</strong> {hoveredEvent.project} <br />
                            <strong>Task Id </strong> {hoveredEvent.task_id} <br />
                            <strong>Hours</strong> {hoveredEvent.hour || "—"} <br />
                            <strong>Description</strong> {hoveredEvent.notes || "—"}
                        </>
                    ) : (
                        <>
                            <strong>Date:</strong> {hoveredEvent.date} <br />
                            <strong>Status:</strong> {hoveredEvent.status} <br />
                            <strong>Punch In:</strong> {hoveredEvent.in_time || "—"} <br />
                            <strong>Punch Out:</strong> {hoveredEvent.out_time || "—"}
                        </>
                    )}
                </div>
            )}

            {/* Hidden Button */}
            <button
                id="openAttendanceModalBtn"
                data-bs-toggle="modal"
                data-bs-target="#attendanceModal"
                style={{ display: "none" }}
            />

            {/* Bootstrap Modal */}
            <div
                className="modal fade"
                id="attendanceModal"
                tabIndex={-1}
                aria-hidden="true"
            >
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">
                                Add Time Log – {dayjs(selectedDate).format("DD MMM YYYY")}
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                            />
                        </div>

                        <div className="modal-body">
                            <div className="mb-3">
                                <label className="form-label">Enter Hours
                                    <sup className="text-danger">*</sup>
                                </label>
                                {/* <TimePicker
                                    defaultValue={dayjs('12:08', 'HH:mm')}
                                    format={'HH:mm'}
                                    value={decimalHoursToHHMM(hours)}
                                    // format="HH:mm"
                                    onChange={(time) => {
                                        setHours(dayjsToDecimalHours(time));
                                    }} /> */}

                                <TimePicker
                                    value={decimalHoursToDayjs(hours)}
                                    format="HH:mm"
                                    onChange={(time) => {
                                        if (!time) return;
                                        setHours(dayjsToDecimalHours(time));
                                    }}
                                />
                                {/* <input
                                    type="time"
                                    lang="en-GB"
                                    className="form-control"
                                    placeholder="HH:MM"
                                    value={decimalHoursToHHMM(hours)}
                                    pattern="^([01]\d|2[0-3]):([0-5]\d)$"
                                    onChange={(e) => {
                                        setHours(hhmmToDecimalHours(e.target.value));
                                    }}
                                /> */}
                                {/* <input
                                    type="time"
                                    className="form-control"
                                    value={decimalHoursToHHMM(hours)} // convert decimal to HH:MM
                                    onChange={(e) => {
                                        setHours(hhmmToDecimalHours(e.target.value));
                                        console.log('Time', hhmmToDecimalHours(e.target.value)) // convert HH:MM → decimal
                                    }}
                                /> */}
                            </div>

                            <div className="mb-3">
                                <label className="form-label">Upload Document
                                    {/* <sup className="text-danger">*</sup> */}
                                </label>
                                <input
                                    type="file"
                                    className="form-control"
                                    multiple
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    onChange={(e) => setAttachment(Array.from(e.target.files || []))}
                                />
                            </div>

                            <div className="mb-3">
                                <label className="form-label">Description
                                    <sup className="text-danger">*</sup>
                                </label>
                                <textarea
                                    className="form-control"
                                    rows={3}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button
                                className="btn btn-secondary mx-2"
                                data-bs-dismiss="modal"
                            >
                                Cancel
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={submitTimeLog}>
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default TimeSheetsLog;